import { Search, Trash2, ShieldCheck, Gauge, Lock } from "lucide-react";
import { Reveal, SectionHeading, waLink } from "./shared";

const STAGES = [
  { icon: Search, title: "Detect", desc: "Deep kernel scan finds hidden threats" },
  { icon: Trash2, title: "Remove", desc: "Eliminate viruses, malware & spyware" },
  { icon: ShieldCheck, title: "Protect", desc: "Shielding & antivirus setup keeps you safe" },
  { icon: Gauge, title: "Optimize", desc: "Restore speed & system stability" },
];

export const Malware = () => (
  <section id="security" data-testid="malware-section" className="relative py-24 sm:py-32 bg-void overflow-hidden">
    <div className="absolute bottom-0 -right-32 w-[30rem] h-[30rem] rounded-full bg-brand-cyan/10 blur-[150px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading
            eyebrow="Virus & Malware Removal"
            title="Clean. Protect."
            accent="Perform."
            sub="Safe browsing · Secure files · Peace of mind. Threats removed without wiping your personal photos, documents or business files."
          />
          <div className="mt-10 space-y-4">
            {STAGES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="group flex items-center gap-5 bg-card/80 border border-slate-800 rounded-xl p-5 hover:border-brand-cyan/60 transition-all duration-300 hover:shadow-[0_0_25px_rgba(34,211,238,0.12)]">
                  <span className="font-display text-3xl font-black text-white/10 group-hover:text-brand-cyan/40 transition-colors leading-none">
                    0{i + 1}
                  </span>
                  <div className="w-12 h-12 shrink-0 rounded-full border-2 border-brand-cyan/60 bg-brand-cyan/10 flex items-center justify-center text-brand-cyan shadow-[0_0_18px_rgba(34,211,238,0.25)] group-hover:scale-110 transition-transform duration-300">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold uppercase text-white">{s.title}</h3>
                    <p className="text-sm text-slate-400">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.35}>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <a
                data-testid="malware-clean-button"
                href={waLink("Hi AFZ Tech Solution, I suspect my device has a virus/malware. I need it cleaned and protected.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-cyan text-void font-display font-bold uppercase px-7 py-3.5 rounded-full hover:bg-cyan-400 hover:scale-[1.03] transition-all duration-300 shadow-[0_0_24px_rgba(34,211,238,0.35)]"
              >
                <ShieldCheck className="w-5 h-5" />
                Sterilize My Device
              </a>
              <div className="flex items-center gap-2 text-slate-400">
                <Lock className="w-4 h-4 text-brand-lime" />
                <span className="text-sm">Zero data-wipe guarantee</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative">
          <div className="relative clip-corner border border-brand-cyan/40 overflow-hidden shadow-[0_0_50px_rgba(34,211,238,0.15)]">
            <img
              src="/assets/collateral-2.jpg"
              alt="Virus and malware removal — detect, remove, protect, optimize"
              className="w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
